import { Navigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { ensureFamily, useGetFamilyByParent } from '~entities/family';

import { StudentByFamilyListPage } from '~features/students/ui/student-by-family-list-page';

import { Button } from '~shared/shadcn/ui/button';

/**
 * V2: classic `/family?parentId=` entry points (Parents list, payments, etc.)
 * resolve to the Jackrabbit-style hub at `/families/:familyId` when a Family
 * exists. Legacy parents without a Family keep the existing workspace; Create
 * family is an upgrade path, not a dead-end (ENR-4054 ship-risk).
 */
export const RedirectFamilyToHub = ({ parentId }: { parentId: string }) => {
  const { data, isLoading, isError, refetch, isFetching } =
    useGetFamilyByParent(parentId);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const familyId = data?.family?._id;

  const onCreateFamily = () => {
    setCreating(true);
    setCreateError(null);
    void ensureFamily({ parentId })
      .then(async () => {
        await refetch();
      })
      .catch(() => {
        setCreateError('Could not create a family for this parent.');
      })
      .finally(() => {
        setCreating(false);
      });
  };

  if (familyId) {
    return (
      <Navigate
        to='/families/$familyId/summary'
        params={{ familyId }}
        replace
      />
    );
  }

  if (isLoading || (isFetching && !data) || creating) {
    return (
      <div className='flex min-h-[40vh] items-center justify-center gap-2 p-8 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        {creating ? 'Creating family…' : 'Opening family…'}
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex min-h-[40vh] items-center justify-center p-8 text-sm text-muted-foreground'>
        Could not load family for this parent.
      </div>
    );
  }

  if (data && data.family === null) {
    return (
      <div className='w-full'>
        <div className='mx-auto flex max-w-3xl flex-col gap-3 border-b bg-muted/30 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-10'>
          <div className='space-y-1'>
            <p className='text-sm font-medium'>
              This parent is not on a Family yet
            </p>
            <p className='text-sm text-muted-foreground'>
              You can keep using tags, payments, and students below. Create a
              Family when you are ready for the multi-guardian hub.
            </p>
            {createError ? (
              <p className='text-sm text-destructive'>{createError}</p>
            ) : null}
          </div>
          <Button
            onClick={onCreateFamily}
            disabled={creating}
            className='shrink-0'
          >
            Create family
          </Button>
        </div>
        <StudentByFamilyListPage parentId={parentId} />
      </div>
    );
  }

  return (
    <div className='flex min-h-[40vh] items-center justify-center gap-2 p-8 text-sm text-muted-foreground'>
      <Loader2 className='size-4 animate-spin' />
      Opening family…
    </div>
  );
};
